import React, { ComponentProps, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import {
  CheckBoxParam,
  Form,
  Option,
  Row,
  Select,
  SelectParam,
  SonForm,
  SubmitButton,
  Switch,
  useForm,
} from '../../../_lib/Form2';
import { PageListType } from '../PageListDetails.utils';
import { Namespace } from '../../../translation/namespace';

export type PageListFormProps = {
  form: SonForm<
    | SelectParam<'pageListDisplayType'>
    | SelectParam<'defaultSortOrder'>
    | CheckBoxParam<'displayAttachment'>
    | SelectParam<'defaultCategoryForChildPages'>
    | SelectParam<'displayedInformationType'>
  >;
};

function PageListDetailsEdit({ form }: PageListFormProps) {
  const { t } = useTranslation('PageListDetails');
  const { delayedRefresh } = useRefreshBlock();

  const { params, formProps, fetchState, hasChanges } = useForm({ form, onSuccess: delayedRefresh });
  const displayTypeParam = params.getByName('pageListDisplayType');
  const defaultSortOrderParam = params.getByName('defaultSortOrder');
  const defaultCategoryForChildPagesParam = params.findByName('defaultCategoryForChildPages');
  const displayAttachmentParam = params.getByName('displayAttachment');
  const displayedInfoParam = params.getByName('displayedInformationType');

  const [selectedDisplayType, setSelectedDisplayType] = useState<string | undefined>(displayTypeParam.value);

  return (
    <Form id="page-list-details-form" {...formProps}>
      <Row>
        <Select
          label={t('inputs.pageListDisplayType.label')}
          param={displayTypeParam}
          onChange={event => setSelectedDisplayType(event.target.value)}
        >
          {displayTypeParam.options.map(option => (
            <Option key={option.value} value={option.value}>
              {t('inputs.pageListDisplayType.options.' + option.value)}
            </Option>
          ))}
        </Select>
      </Row>
      <Row>
        <Select label={t('inputs.pageListDisplayInformation.label')} param={displayedInfoParam}>
          {displayedInfoParam.options.map(option => (
            <Option key={option.value} value={option.value}>
              {t('inputs.pageListDisplayInformation.options.' + option.value)}
            </Option>
          ))}
        </Select>
      </Row>
      <Row>
        <Select
          label={t('inputs.defaultSortOrder.label')}
          param={defaultSortOrderParam}
          disabled={selectedDisplayType === PageListType.ATOZ}
        >
          {defaultSortOrderParam.options.map(option => (
            <Option key={option.value} value={option.value}>
              {t('inputs.defaultSortOrder.options.' + option.value)}
            </Option>
          ))}
        </Select>
      </Row>
      {defaultCategoryForChildPagesParam && (
        <Row>
          <Select label={t('inputs.defaultCategoryForChildPages.label')} param={defaultCategoryForChildPagesParam}>
            <Option key="default" value="">
              {t(`${Namespace.phrases}:selectPlaceholderOptionNotRequiredLabel`)}
            </Option>
            {defaultCategoryForChildPagesParam.options.map(option => (
              <Option key={option.value} value={option.value}>
                {option.text}
              </Option>
            ))}
          </Select>
        </Row>
      )}
      <Row>
        <Switch label={t('inputs.displayAttachment.label')} param={displayAttachmentParam} />
      </Row>
      <SubmitButton
        buttonLabel={t('phrases:saveSettings')}
        messageSuccess={t('phrases:changeSuccesfull')}
        messageSending={t('phrases:processIsCurrentlyRunningWithDots')}
        messageFail={t('phrases:changesCouldNotBeSaved')}
        fetchState={fetchState}
        disabled={fetchState.type === 'success' || fetchState.type === 'loading' || !hasChanges}
      />
    </Form>
  );
}

export const PageListDetailsEditView = asFetchableView(PageListDetailsEdit);
export type PageListDetailsEditView = ComponentProps<typeof PageListDetailsEditView>;
